/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { screen } from "@monsma/XDL/Helpers";
import { xBaseTheme } from "@monsma/XDL/types";
import { css } from "styled-components";

const MODAL_DESKTOP_PADDING = 20;
const MODAL_MOBILE_PADDING = 20;

export default {
    sizeVariants: {
        small: 350,
        medium: 720,
        large: 1200,
    },
    defaultProps: {
        cssOverrides: {
            modalBody: css`
                background: ${getColor("text.inverse")};
                overflow: hidden;
                box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.5);
                border-radius: 6px;
                overflow: unset;

                ${screen("lte", "md")} {
                    max-width: 90%;
                    height: auto;
                    max-height: calc(100% - 35px);
                    margin: 15px;
                }
            `,
            headlineTypography: css`
                margin-bottom: 15px;
                padding-bottom: 10px;
                display: inline-block;
                border-bottom: 1px solid ${getColor("common.ui12")};
                width: 100%;
                line-height: 26px;
            `,
            titleButton: css`
                && {
                    line-height: 1;
                    position: absolute;
                    top: -13px;
                    right: -13px;
                    margin: 0;
                    border: 3px solid ${getColor("text.inverse")};
                    background-color: ${getColor("danger.main")};
                    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
                    border-radius: 50%;
                    width: 42px;
                    padding: 0;

                    > span {
                        position: unset;
                        top: 0;
                    }

                    > span,
                    svg {
                        color: ${getColor("text.inverse")};
                        stroke-width: 4px;
                    }

                    &:hover,
                    &:focus {
                        border-color: ${getColor("text.inverse")};
                        background: ${getColor("common.ui16")};
                    }
                }
            `,
            modalTitle: css`
                border: none;
                padding: 30px ${MODAL_DESKTOP_PADDING}px 0 ${MODAL_DESKTOP_PADDING}px;

                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-left: ${MODAL_MOBILE_PADDING}px;
                                padding-right: ${MODAL_MOBILE_PADDING}px;

                                ${TypographyStyle as any} {
                                    font-size: 24px;
                                    line-height: 28px;
                                }
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )};
            `,
            modalContent: css`
                padding: 0px ${MODAL_DESKTOP_PADDING}px 0 ${MODAL_DESKTOP_PADDING}px;
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow-y: auto;

                &:after {
                    content: "";
                    display: block;
                    padding-bottom: 25px;
                    z-index: -1;
                    pointer-events: none;
                }

                .modal-actions {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    width: 100%;
                    margin-top: 30px;

                    background: ${getColor("common.background")};

                    button {
                        margin: 0;
                        margin-left: 10px;
                    }

                    &.grid-wrapper {
                        margin-top: 15px;
                    }
                }
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding-left: ${MODAL_MOBILE_PADDING}px;
                                padding-right: ${MODAL_MOBILE_PADDING}px;

                                .modal-actions {
                                    flex-direction: column;
                                    justify-content: center;

                                    button {
                                        margin: 10px 0 0 0;
                                        width: 100%;

                                        &:first-child {
                                            margin-top: 0;
                                        }
                                    }
                                }
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )};
            `,
        },
        sizeVariant: "medium",
        closeButtonProps: {
            buttonType: "solid",
            sizeVariant: "small",
            shape: "pill",
            variant: "primary",
            color: "text.inverse",
            size: 42,
        },
        closeButtonIconProps: {
            size: 20,
            color: "text.inverse",
        },
        headlineTypographyProps: {
            variant: "h4",
            forwardAs: "h3",
        },
    },
} as xBaseTheme["modal"];
